import { Clock, Plus, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { StudioPageLayout } from "@/components/flowly/StudioPageLayout/StudioPageLayout";
import { ImageLightbox } from "@/components/flowly/Lightbox/ImageLightbox/ImageLightbox";
import { CreationsGallery } from "@/components/flowly/CreationsGallery/CreationsGallery";
import { MediaGridItem } from "@/components/flowly/MediaGridItem/MediaGridItem";
import { GalleryItemBadges } from "@/components/flowly/GalleryItemBadges/GalleryItemBadges";
import { Button } from "@/components/flowly/infra/Button/Button";
import { HowItWorks } from "@/components/flowly/HowItWorks/HowItWorks";
import { ChatInputBar } from "@/components/flowly/ImageDirectorChat/ChatInputBar";
import { HistoryPanel } from "@/components/flowly/ImageDirectorChat/HistoryPanel";
import { ScrollToBottomButton } from "@/components/flowly/ImageDirectorChat/ScrollToBottomButton";
import { ChatMessages } from "@/components/flowly/ImageDirectorChat/ChatMessages";
import { GenerationBar } from "@/components/flowly/GenerationBar/GenerationBar";
import { ReferenceToggle } from "@/components/flowly/ImageDirectorChat/ReferenceToggle";
import { downloadMedia } from "@/lib/mediaUtils";
import { useImageDirectorChat } from "./useImageDirectorChat";
import { MAX_REFERENCE_IMAGES } from "./types";

export default function ImageDirectorChat() {
  const { t } = useLanguage();

  const {
    messagesEndRef,
    chatContainerRef,
    locale,
    messages,
    inputValue,
    setInputValue,
    isLoading,
    isReadyToGenerate,
    sendMessage,
    useReferenceImages,
    referenceImages,
    handleToggleReference,
    handleImagesChange,
    generating,
    quantity,
    setQuantity,
    resolution,
    availableResolutions,
    setResolution,
    aspectRatio,
    setAspectRatio,
    pendingCount,
    directorPendingImages,
    directorCompletingIds,
    handleGenerate,
    getCreditCost,
    getDirectorCost,
    getImageModelCost,
    costsLoading,
    showScrollButton,
    newMessagesCount,
    handleScroll,
    scrollToBottom,
    showHistory,
    setShowHistory,
    viewingHistoryId,
    handleSelectConversation,
    handleResumeConversation,
    startNewConversation,
    models,
    modelWarning,
    imageModel,
    handleImageModelChange,
    directorImages,
    directorFailedImages,
    handleClearFailed,
    selectedImage,
    setSelectedImage,
    selectedImageIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,
    handleDeleteImage,
    handleDownloadImage,
    handleShareImage,
    handleAnimateImage,
    availableAspectRatios,
    handleRegenerateAsReference,
    handleDisabledClick,
  } = useImageDirectorChat();

  return (
    <StudioPageLayout
      activeTab="images"
      activeApp="director"
      seoHeadingKey="imageDirector"
      headerChildren={
        <>
          <HowItWorks flowId="directorChat" />
          <button
            onClick={() => setShowHistory(true)}
            className="w-10 h-10 bg-secondary rounded-xl flex items-center justify-center hover:bg-muted transition-colors border border-border text-muted-foreground hover:text-foreground"
            title={t.director.conversationHistory}
          >
            <Clock className="w-5 h-5" />
          </button>
          <Button
            variant="primary"
            size="xs"
            rounded="xl"
            onClick={startNewConversation}
          >
            <Plus className="w-4 h-4" />
            {t.director.newChat}
          </Button>
        </>
      }
      footer={
        <GenerationBar
          onGenerate={handleGenerate}
          generating={generating}
          disabled={!!viewingHistoryId || !isReadyToGenerate}
          creditCost={getCreditCost()}
          costsLoading={costsLoading}
          buttonLabel={t.director.generate}
          buttonTooltip={
            !isReadyToGenerate ? t.director.promptNotReady : undefined
          }
          aspectRatio={aspectRatio}
          onAspectRatioChange={(v) => setAspectRatio(v as typeof aspectRatio)}
          availableAspectRatios={availableAspectRatios}
          resolution={resolution}
          onResolutionChange={(v) => setResolution(v as typeof resolution)}
          availableImageResolutions={availableResolutions}
          quantity={quantity}
          onQuantityChange={setQuantity}
          modelWarning={modelWarning}
          selectedModelId={imageModel}
          onModelChange={handleImageModelChange}
          mediaType="image"
          getModelCost={(id) => getImageModelCost(id, models, resolution, "director_generate")}
          onDisabledClick={handleDisabledClick}
        />
      }
      lightbox={
        selectedImage ? (
          <ImageLightbox
            imageUrl={selectedImage.url}
            imageId={selectedImage.id}
            referenceImageUrl={selectedImage.referenceImageUrls[0]}
            createdAt={selectedImage.createdAt}
            resolution={selectedImage.resolution}
            aspectRatio={selectedImage.aspectRatio}
            pipeline={selectedImage.pipeline}
            prompt={selectedImage.prompt}
            model={selectedImage.model}
            onClose={() => setSelectedImage(null)}
            onRegenerate={handleRegenerateAsReference}
            onAnimate={handleAnimateImage}
            onDownload={handleDownloadImage}
            onShare={handleShareImage}
            onDelete={() => handleDeleteImage(selectedImage!.url)}
            navigation={
              directorImages.length > 1
                ? {
                    currentIndex: selectedImageIndex,
                    totalItems: directorImages.length,
                    canGoNext: canNavigate,
                    canGoPrev: canNavigate,
                    onNext: goToNextItem,
                    onPrev: goToPrevItem,
                  }
                : undefined
            }
          />
        ) : undefined
      }
      overlays={
        <HistoryPanel
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          onSelectConversation={handleSelectConversation}
          onResumeConversation={handleResumeConversation}
        />
      }
    >
      {/* Chat Messages */}
      <div
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="overflow-y-auto pb-4 h-[300px] relative bg-secondary/20 border border-border/50 rounded-2xl p-4"
      >
        <ChatMessages messages={messages} isLoading={isLoading} />
        <div ref={messagesEndRef} />

        <ScrollToBottomButton
          isVisible={showScrollButton}
          onClick={() => scrollToBottom()}
          newMessagesCount={newMessagesCount}
        />
      </div>

      {/* Chat Input + Reference Toggle */}
      <div className="relative" data-scroll-target="chat-input">
        <ReferenceToggle
          useReferenceImages={useReferenceImages}
          onToggle={handleToggleReference}
          disabled={referenceImages.length === 0}
        />

        <ChatInputBar
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSend={sendMessage}
          onImagesChange={handleImagesChange}
          referenceImages={referenceImages}
          maxImages={MAX_REFERENCE_IMAGES}
          disabled={isLoading || !!viewingHistoryId}
          placeholder={
            viewingHistoryId
              ? t.director.viewingHistory
              : t.director.describePlaceholder
          }
        />
      </div>

      <CreationsGallery
        title={t.director.allAgentImages}
        pendingCount={pendingCount}
        pendingLabel={t.director.processing}
        pendingItems={directorPendingImages.map((img) => ({ id: img.id, createdAt: img.created_at }))}
        pendingMediaType="image"
        completingIds={directorCompletingIds}
        failedItems={directorFailedImages}
        failedLabel={t.director.failedRefunded}
        onClearFailed={handleClearFailed}
        emptyIcon={Sparkles}
        emptyTitle={t.director.noImagesYet}
        emptySubtitle={t.director.startCreating}
        className="mt-10 mb-10"
      >
        {directorImages.map((img) => (
          <MediaGridItem
            key={img.id}
            imageUrl={img.displayUrl}
            hoverText={t.director.clickToOpen}
            alt="Generated"
            onClick={() => setSelectedImage(img)}
            onDownload={() => downloadMedia(img.url, "director-image", "png")}
            downloadLabel={t.lightbox.download}
          >
            <GalleryItemBadges item={img} />
          </MediaGridItem>
        ))}
      </CreationsGallery>
    </StudioPageLayout>
  );
}
